import React, { useCallback, useEffect, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import api from '../../api';
import { extractError } from '../../api/client';
import { toast, confirmDelete, showError } from '../../utils/alert';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import BulkActions from '../../components/common/BulkActions';
import Pagination from '../../components/common/Pagination';
import StatusBadge from '../../components/common/StatusBadge';
import { formatMoney } from '../../utils/format';

export default function Payments() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [params, setParams] = useState({ page: 1, per_page: 100 });
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.invoices.payments.list(params);
      const list = Array.isArray(data.data) ? data.data : data.payments || data;
      setRows(Array.isArray(list) ? list : list.data || []);
      setMeta(data.data ? { current_page: data.current_page, last_page: data.last_page, total: data.total, per_page: data.per_page, per_page_options: data.data.per_page_options } : null);
    } catch (err) {
      showError('Failed to load payments', extractError(err));
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (row) => {
    const res = await confirmDelete('this payment');
    if (!res.isConfirmed) return;
    try {
      await api.invoices.payments.remove(row.id);
      toast('success', '', 'Payment deleted & voucher voided');
      load();
    } catch (err) {
      showError('Failed to delete payment', extractError(err));
    }
  };

  return (
    <div className="page">
      <PageHeader
        title="Payments"
        subtitle="All invoice payments received."
        crumbs={[{ label: 'Finance', to: '/invoices' }, { label: 'Invoices', to: '/invoices' }, { label: 'Payments' }]}
      />

      <div className="card">
        <div className="card-body">
          <BulkActions selectedIds={selectedIds} onClear={() => setSelectedIds([])} onDone={load} deleteType="invoices" label="payments" />
          <DataTable
            loading={loading}
            data={rows}
            emptyTitle="No payments found"
            selectable
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            columns={[
              { key: 'id', label: 'Payment No', render: (r) => <b>#{r.id}</b> },
              { key: 'payment_date', label: 'Date' },
              { key: 'invoice', label: 'Invoice', render: (r) => (r.invoice ? r.invoice.invoice_no : '-') },
              { key: 'party', label: 'Party', render: (r) => {
                const inv = r.invoice || {};
                if (inv.invoice_type === 'purchase') return inv.supplier ? inv.supplier.name : '-';
                return inv.party ? inv.party.name : '-';
              } },
              { key: 'payment_type', label: 'Type', render: (r) => <StatusBadge status={r.payment_type} /> },
              { key: 'amount', label: 'Amount', align: 'right', render: (r) => formatMoney(r.amount) },
              { key: 'net_amount', label: 'Net', align: 'right', render: (r) => formatMoney(r.net_amount) },
              { key: 'id', label: '', align: 'right', render: (r) => (
                <div className="actions">
                  <button className="btn-icon danger" onClick={() => handleDelete(r)}><FiTrash2 /></button>
                </div>
              ) },
            ]}
          />
          {meta && <Pagination meta={meta} onPage={(p) => setParams({ ...params, page: p })} onPerPage={(n) => setParams({ ...params, per_page: n, page: 1 })} />}
        </div>
      </div>
    </div>
  );
}